import { LogLevel } from '@environment/enums/log-level.enum';
import { postToBackendFromLocalStorage } from '@shared/http/utils/post-to-backend-from-local-storage';
import { captureSentryMessage } from '@shared/sentry';
import { ONE_SECOND_MS } from '@shared/utils/constants';
import { BUFFERED_REQUEST_STORAGE_PREFIX, METADATA_STORAGE_PREFIX } from '../constants';

const DELAYED_STORED_REQUESTS_AGE = ONE_SECOND_MS * 5;
const MAX_EVENT_AGE = ONE_SECOND_MS * 60 * 60 * 3; // 3 hours

export async function getDelayedStoredRequests(): Promise<string[]> {
  try {
    const items = await getAllBufferedRequests();
    const delayedStoredRequests = items.filter(key => {
      const keyParts = key.split('::');
      const age = Date.now() - parseInt(keyParts[keyParts.length - 1]);
      return age > DELAYED_STORED_REQUESTS_AGE;
    });
    captureSentryMessage(
      `Found ${delayedStoredRequests.length} delayed stored requests`,
      {
        source: 'stored-requests',
      },
      LogLevel.Debug
    );
    return delayedStoredRequests;
  } catch (e) {
    console.error('Error while getting delayed stored requests', e);
    return [];
  }
}

export async function dropOldStoredRequests(): Promise<number> {
  try {
    const items = await getAllBufferedRequests();
    const oldKeys = items.filter(key => {
      const keyParts = key.split('::');
      const age = Date.now() - parseInt(keyParts[keyParts.length - 1]);
      return age > MAX_EVENT_AGE;
    });

    if (oldKeys.length > 0) {
      captureSentryMessage(
        `Dropping ${oldKeys.length} requests older than ${MAX_EVENT_AGE / ONE_SECOND_MS / 60 / 60} hours`
      );
      await chrome.storage.local.remove(oldKeys);
    }

    // Remove all metadata, not needed now as we have added debug logs
    await removeAllMetadata();

    return oldKeys.length;
  } catch (e) {
    console.error('Error while dropping old stored requests', e);
  }
}

export async function processDelayedStoredRequests(backendUrl: string, source: string): Promise<void> {
  // First drop very old requests
  const dropped = await dropOldStoredRequests();

  const delayedStoredRequests = await getDelayedStoredRequests();
  captureSentryMessage(
    `Processing ${delayedStoredRequests.length} delayed stored requests after dropping ${dropped} old requests from ${source}`,
    {
      source,
    },
    LogLevel.Debug
  );

  if (delayedStoredRequests.length === 0) {
    return;
  }

  for (const key of delayedStoredRequests) {
    await postToBackendFromLocalStorage(key, backendUrl, source);
  }
}

export async function removeAllMetadata(): Promise<number> {
  // TODO: Remove this function later
  try {
    const items = await chrome.storage.local.get();
    const metadataKeys = Object.keys(items).filter(key => key.startsWith(METADATA_STORAGE_PREFIX));
    captureSentryMessage(`Removing ${metadataKeys.length} metadata entries`, {}, LogLevel.Debug);

    if (metadataKeys.length > 0) {
      await chrome.storage.local.remove(metadataKeys);
    }

    return metadataKeys.length;
  } catch (e) {
    console.error('Error while removing metadata', e);
    captureSentryMessage('Failed to remove metadata', { error: e }, LogLevel.Warning);
    return 0;
  }
}

/**
 * Returns all buffered request keys from local storage
 * @returns Array of buffered request keys
 */
export async function getAllBufferedRequests(): Promise<string[]> {
  try {
    const items = await chrome.storage.local.get();
    return Object.keys(items).filter(key => key.startsWith(BUFFERED_REQUEST_STORAGE_PREFIX));
  } catch (e) {
    console.error('Error while getting buffered requests', e);
    captureSentryMessage('Failed to get buffered requests', { error: e }, LogLevel.Warning);
    return [];
  }
}

/**
 * Returns the count of buffered requests in storage
 * @returns Number of buffered requests
 */
export async function getBufferedRequestCount(): Promise<number> {
  const keys = await getAllBufferedRequests();
  return keys.length;
}
